"use client";

import { useLocale, useTranslations } from "use-intl";

import { offerMovementSignal } from "@/components/marketplace/deal-offers-utils";
import { OfferPriceRangeScale } from "@/components/marketplace/offer-price-range-scale";
import { offerSummaryPriceDisplay } from "@/components/marketplace/offer-summary-price-display";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";

export type OfferSummary = RouterOutputs["deals"]["getOfferSummaries"][number];
export type OfferSummaryBadgeMode = "work" | "release";
export type OfferSummaryBadgeVariant = "default" | "signal";

interface OfferSummaryBadgeProps {
  className?: string;
  mode?: OfferSummaryBadgeMode;
  offerSummary: OfferSummary | null | undefined;
  releaseId?: string | null;
  variant?: OfferSummaryBadgeVariant;
}

export function OfferSummaryBadge({
  className,
  offerSummary,
  variant = "default",
}: OfferSummaryBadgeProps) {
  const t = useTranslations("shared.affiliates");
  const locale = useLocale();

  if (!offerSummary) return null;

  const priceDisplay = offerSummaryPriceDisplay(offerSummary);
  const movementSignal = offerMovementSignal(priceDisplay.movementSource, locale, t);
  const movement = movementSignal?.kind === "flat" ? null : movementSignal;

  if (variant === "signal") {
    if (!priceDisplay.priceInsightsVisible) return null;
    if (!movement) return null;
    const MovementIcon = movement.meta.icon;

    return (
      <div
        className={cn("flex w-full flex-nowrap items-center gap-1.5 overflow-hidden", className)}
      >
        <span
          className={cn(
            "inline-flex min-h-4 shrink-0 items-center gap-1 text-xs font-black tabular-nums",
            movement.meta.className,
          )}
          title={`${movement.meta.label} ${movement.percent}`}
        >
          <MovementIcon className="text-base leading-none" />
          {movement.percent}
        </span>
      </div>
    );
  }

  return (
    <OfferPriceRangeScale
      className={className}
      price={{
        currentPrice: offerSummary.bestOfferPrice,
        dealReferencePrice: priceDisplay.dealReferencePrice,
      }}
      lowestPrice={priceDisplay.lowestPrice}
      averagePrice={priceDisplay.averagePrice}
      highestPrice={priceDisplay.highestPrice}
      currency={offerSummary.priceHistoryCurrency ?? offerSummary.currency}
      movementSource={priceDisplay.movementSource}
      priceIndicators={priceDisplay.priceIndicators}
      compactSignal
      showRangeBar={false}
      showRangeLabels={false}
      showReferencePrice={false}
    />
  );
}
